import React, { useState, useMemo } from 'react';
import { Card, Carousel, Alert, Spin } from 'antd';
import { PlayCircleOutlined, CameraOutlined } from '@ant-design/icons';
import PdfSlideshow from './PdfSlideshow';
import VideoJS from '../container/function/VideoJS';
import useStreamState from '../hooks/useStreamState';

const MediaCard = ({ mediaConfig, sbTheme = false, SB = {} }) => {
  const { title, content_type = 'camera', camera_id, media_urls = [], carousel_interval = 5 } = mediaConfig || {};
  const [videoError, setVideoError] = useState(false);

  // Camera stream state — hook is always called, cameraId is null when not camera mode
  const { posterUrl, posterLoaded, setPosterLoaded, posterBust, streamStatus, hlsUrl } =
    useStreamState(content_type === 'camera' ? camera_id : null);

  const videoJsOptions = useMemo(() => ({
    autoplay: true,
    controls: true,
    responsive: true,
    fill: true,
    sources: [{ src: hlsUrl, type: 'application/x-mpegURL' }],
    html5: {
      vhs: {
        withCredentials: false,
        enableLowInitialPlaylist: true,
        smoothQualityChange: true,
        overrideNative: true,
      },
      nativeAudioTracks: false,
      nativeVideoTracks: false,
    },
    liveui: true,
    liveTracker: { trackingThreshold: 0, liveTolerance: 15 },
  }), [hlsUrl]);

  // 渲染攝影機串流
  const renderCamera = () => {
    if (!camera_id) {
      return (
        <div style={{ textAlign: "center", color: "#8c8c8c", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 120, height: 120, borderRadius: "50%", backgroundColor: sbTheme ? "#2a3142" : "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <CameraOutlined style={{ fontSize: 48, color: sbTheme ? "#3a4152" : "#8c8c8c" }} />
          </div>
          <div style={{ fontSize: 14, marginBottom: 4 }}>即時影像串流</div>
          <div style={{ fontSize: 12, color: "#666" }}>未設定來源</div>
        </div>
      );
    }

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* 離線 banner：有最後畫面時才顯示 */}
        {posterLoaded && streamStatus === 'offline' && (
          <Alert
            type="warning"
            showIcon
            message="串流離線，顯示最後畫面"
            style={{ flexShrink: 0, margin: 0, borderRadius: 0, border: 'none', borderBottom: '1px solid #ffe58f' }}
          />
        )}

        {/* 影像區域 */}
        <div style={{ position: 'relative', flex: 1, background: '#000', minHeight: 0 }}>
          {streamStatus === 'active' && hlsUrl ? (
            <VideoJS options={videoJsOptions} />
          ) : (
            <>
              {/* 最後畫面 */}
              {posterUrl && (
                <img
                  src={`${posterUrl}?b=${posterBust}`}
                  alt=""
                  onLoad={() => setPosterLoaded(true)}
                  onError={() => setPosterLoaded(false)}
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'contain',
                    display: posterLoaded ? 'block' : 'none',
                  }}
                />
              )}
              {/* 沒有最後畫面時顯示 spinner 或離線圖示 */}
              {!posterLoaded && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {streamStatus === 'loading' ? (
                    <Spin />
                  ) : (
                    <>
                      <div style={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: sbTheme ? '#2a3142' : '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                        <CameraOutlined style={{ fontSize: 36, color: sbTheme ? '#4a5162' : '#8c8c8c' }} />
                      </div>
                      <div style={{ fontSize: 13, color: sbTheme ? '#8c8c8c' : '#666' }}>串流離線</div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // 渲染靜態圖片
  const renderImage = () => {
    if (!media_urls || media_urls.length === 0) {
      return (
        <div style={{ textAlign: "center", color: "#8c8c8c", padding: "60px 20px" }}>
          <div style={{ fontSize: 14 }}>尚未上傳圖片</div>
        </div>
      );
    }

    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img
          src={media_urls[0]}
          alt={title}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <div style={{ display: 'none', textAlign: 'center', color: '#8c8c8c' }}>
          圖片載入失敗
        </div>
      </div>
    );
  };

  // 渲染圖片輪播
  const renderCarousel = () => {
    if (!media_urls || media_urls.length === 0) {
      return (
        <div style={{ textAlign: "center", color: "#8c8c8c", padding: "60px 20px" }}>
          <div style={{ fontSize: 14 }}>尚未上傳圖片</div>
        </div>
      );
    }

    return (
      <div style={{ width: "100%" }}>
        <div style={{ display: 'none' }}>
          {media_urls.map((url, i) => <img key={i} src={url} alt="" />)}
        </div>
        <Carousel autoplay autoplaySpeed={carousel_interval * 1000}>
          {media_urls.map((url, index) => (
            <div key={index}>
              <div style={{ height: "320px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#1a1f2e" }}>
                <img
                  src={url}
                  alt={`${title} - ${index + 1}`}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              </div>
            </div>
          ))}
        </Carousel>
      </div>
    );
  };

  // 渲染影片播放
  const renderVideo = () => {
    if (!media_urls || media_urls.length === 0) {
      return (
        <div style={{ textAlign: "center", color: "#8c8c8c", padding: "60px 20px" }}>
          <PlayCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
          <div style={{ fontSize: 14 }}>尚未上傳影片</div>
        </div>
      );
    }

    if (videoError) {
      return (
        <div style={{ textAlign: "center", color: "#ff4d4f", padding: "60px 20px" }}>
          <div style={{ fontSize: 14 }}>影片載入失敗</div>
          <div style={{ fontSize: 12, marginTop: 8 }}>請檢查影片格式或檔案</div>
        </div>
      );
    }

    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <video
          src={media_urls[0]}
          controls
          autoPlay
          loop
          muted
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          onError={() => setVideoError(true)}
        />
      </div>
    );
  };

  // 渲染 PDF 投影片
  const renderSlideshow = () => {
    if (!media_urls || media_urls.length === 0) {
      return (
        <div style={{ textAlign: "center", color: "#8c8c8c", padding: "60px 20px" }}>
          <div style={{ fontSize: 14 }}>尚未上傳 PDF</div>
        </div>
      );
    }
    return (
      <div style={{ width: '100%', height: '100%', overflowY: 'auto', display: 'flex', justifyContent: 'center' }}>
        <PdfSlideshow
          url={media_urls[0]}
          autoPlay={carousel_interval > 0}
          interval={carousel_interval}
        />
      </div>
    );
  };

  const renderContent = () => {
    switch (content_type) {
      case 'image':    return renderImage();
      case 'carousel': return renderCarousel();
      case 'video':    return renderVideo();
      case 'slideshow': return renderSlideshow();
      case 'camera':
      default:         return renderCamera();
    }
  };

  const isCameraWithId = content_type === 'camera' && camera_id;

  return (
    <Card
      title={<span style={{ color: sbTheme ? SB.text : undefined }}>{title}</span>}
      style={{
        height: "400px",
        backgroundColor: sbTheme ? SB.card : "#ffffff",
        backdropFilter: sbTheme ? SB.backdrop : undefined,
        borderRadius: "12px",
        border: sbTheme ? SB.border : undefined,
        boxShadow: sbTheme ? SB.glow : undefined,
      }}
      styles={{
        header: sbTheme ? { background: SB.card, borderBottom: 'none', color: SB.text } : {},
        body: {
          height: "calc(100% - 57px)",
          display: "flex",
          ...(isCameraWithId
            ? { padding: 0, flexDirection: 'column', alignItems: 'stretch' }
            : { alignItems: 'center', justifyContent: 'center' }
          ),
          backgroundColor: sbTheme ? (content_type === 'camera' ? "#0a0f1e" : SB.card) : "#ffffff",
          borderTop: sbTheme ? SB.border : '1px solid #f0f0f0',
          borderRadius: "0 0 12px 12px",
          overflow: "hidden",
        },
      }}
    >
      {renderContent()}
    </Card>
  );
};

export default MediaCard;
